import { parse, isValid } from "date-fns";
import { sql } from "drizzle-orm";
import { pgTable, text, varchar, timestamp, integer, boolean, numeric } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

export const users = pgTable("users", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  username: text("username").notNull().unique(),
  password: text("password").notNull(),
  savedPassword: text("saved_password"), // Сохранённый пароль для автозаполнения
  role: text("role").notNull(),
  name: text("name").notNull(),
  isBlocked: boolean("is_blocked").default(false),
  agreementAccepted: boolean("agreement_accepted").default(false),
  agreementAcceptedAt: timestamp("agreement_accepted_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const userProfiles = pgTable("user_profiles", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  companyName: varchar("company_name", { length: 255 }).notNull(),
  companyInn: varchar("company_inn", { length: 12 }).notNull(),
  companyKpp: varchar("company_kpp", { length: 9 }),
  companyOgrn: varchar("company_ogrn", { length: 15 }),
  email: varchar("email", { length: 255 }),
  phone: varchar("phone", { length: 20 }),
  legalAddress: text("legal_address"),
  actualAddress: text("actual_address"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const cargoRequests = pgTable("cargo_requests", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  number: text("number").notNull().unique(),
  status: text("status").notNull().default("draft"), // draft, review, published, in_trading, completed, cancelled, rejected

  // Route information
  departureStation: text("departure_station").array(), // Станция погрузки (multiple)
  departureRailway: text("departure_railway"), // Дорога погрузки (auto-filled)
  arrivalStation: text("arrival_station").array(), // Станция выгрузки (multiple)
  arrivalRailway: text("arrival_railway"), // Дорога выгрузки (auto-filled)
  routePrices: text("route_prices").array(), // Цены для каждого маршрута (соответствуют станциям)
  stationWagonCounts: text("station_wagon_counts").array(), // Количество вагонов для каждой станции выгрузки
  additionalRailways: text("additional_railways").array(), // Дополнительные дороги выгрузки

  // Cargo information
  cargoName: text("cargo_name").notNull(),
  etsngsCode: text("etsngs_code").notNull(),
  cargoWeight: integer("cargo_weight").notNull(),
  wagonType: text("wagon_type").notNull(),
  wagonModel: text("wagon_model"), // Модель вагона (auto-filled based on type)
  additionalWagonRequirements: text("additional_wagon_requirements"), // Дополнительные требования по вагонам

  // Schedule and norms
  plannedDeparture: timestamp("planned_departure").notNull(),
  deliveryDays: integer("delivery_days"),
  loadingNorm: integer("loading_norm"),
  unloadingNorm: integer("unloading_norm"),
  wagonCount: integer("wagon_count"),

  // Additional transportation parameters
  penaltyRate: numeric("penalty_rate", { precision: 10, scale: 2 }),
  penaltyAmount: integer("penalty_amount"), // Штраф за превышение сроков норматива
  moratoriumActive: boolean("moratorium_active").default(false), // Мораторий
  wagonSubmissionFrom: timestamp("wagon_submission_from"),
  wagonSubmissionTo: timestamp("wagon_submission_to"),
  wagonSchedulePeriod: integer("wagon_schedule_period"), // декада
  wagonScheduleCount: integer("wagon_schedule_count"),
  wagonRequirements: text("wagon_requirements"),
  specialNotes: varchar("special_notes", { length: 500 }),
  tradingCloseDateTime: timestamp("trading_close_datetime"),

  // Этапы торгов
  tradingStartDate: timestamp("trading_start_date"),
  tradingEndDate: timestamp("trading_end_date"),
  firstStageEndDate: timestamp("first_stage_end_date"),
  secondStageActive: boolean("second_stage_active").default(false),
  secondStageStartDate: timestamp("second_stage_start_date"),
  secondStageEndDate: timestamp("second_stage_end_date"),
  thirdStageStartDate: timestamp("third_stage_start_date"),
  thirdStageEndDate: timestamp("third_stage_end_date"),

  // Wagon owner specific fields
  orderPeriod: text("order_period"),
  provisionPeriod: text("provision_period"),
  loadingPeriod: text("loading_period"),
  restrictionsNotes: text("restrictions_notes"),

  // Customer information
  customerName: text("customer_name"), // Наименование КА (Заказчик)
  customerInn: text("customer_inn"), // ИНН (Заказчик)

  // System fields
  createdBy: varchar("created_by")
    .notNull()
    .references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  moderatedBy: varchar("moderated_by").references(() => users.id),
  moderatedAt: timestamp("moderated_at"),
  moderatorComment: text("moderator_comment"), // Комментарий модератора при отклонении
});

// Trading offers table
export const tradingOffers = pgTable("trading_offers", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  cargoRequestId: varchar("cargo_request_id")
    .notNull()
    .references(() => cargoRequests.id, { onDelete: "cascade" }),
  offererId: varchar("offerer_id")
    .notNull()
    .references(() => users.id),
  pricePerWagon: numeric("price_per_wagon", { precision: 10, scale: 2 }),
  availableWagons: integer("available_wagons"),
  specialConditions: text("special_conditions"),
  status: text("status").notNull().default("approved"), // approved, rejected, withdrawn
  isAnonymous: boolean("is_anonymous").default(true),
  moderatedBy: varchar("moderated_by").references(() => users.id),
  moderatedAt: timestamp("moderated_at"),
  rejectionReason: text("rejection_reason"),

  // Поля для переторжки
  isRetrade: boolean("is_retrade").default(false), // Является ли предложением переторжки
  originalOfferId: varchar("original_offer_id"), // ID оригинального предложения (строка для избежания циклической зависимости)
  retradeTariff: numeric("retrade_tariff", { precision: 10, scale: 2 }), // Новый тариф переторжки
  retradeComment: text("retrade_comment"), // Комментарий к переторжке

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Retrades table - информация о переторжках
export const retrades = pgTable("retrades", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  cargoRequestId: varchar("cargo_request_id")
    .notNull()
    .references(() => cargoRequests.id, { onDelete: "cascade" }),
  type: text("type").notNull(), // "free_format" | "descending_tender"
  departureStation: text("departure_station").array(), // Станции погрузки для переторжки
  arrivalStation: text("arrival_station").array(), // Станции выгрузки для переторжки
  startDateTime: timestamp("start_date_time").notNull(), // Дата и время начала переторжки
  endDateTime: timestamp("end_date_time").notNull(), // Дата и время окончания переторжки
  status: text("status").notNull().default("active"), // active, completed, cancelled
  createdBy: varchar("created_by")
    .notNull()
    .references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Retrade offers table - предложения в переторжках
export const retradeOffers = pgTable("retrade_offers", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  retradeId: varchar("retrade_id")
    .notNull()
    .references(() => retrades.id, { onDelete: "cascade" }),
  cargoRequestId: varchar("cargo_request_id")
    .notNull()
    .references(() => cargoRequests.id, { onDelete: "cascade" }),
  offererId: varchar("offerer_id")
    .notNull()
    .references(() => users.id),
  pricePerWagon: numeric("price_per_wagon", { precision: 10, scale: 2 }).notNull(),
  availableWagons: integer("available_wagons").notNull(),
  departureStation: text("departure_station").array(),
  specialConditions: text("special_conditions"),
  status: text("status").notNull().default("pending"), // pending, approved, rejected, withdrawn
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Notifications table
export const notifications = pgTable("notifications", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  type: text("type").notNull(), // new_request, new_offer, trading_started, trading_ended, retrade_started, etc.
  title: text("title").notNull(),
  message: text("message").notNull(),
  isRead: boolean("is_read").default(false),
  relatedRequestId: varchar("related_request_id").references(() => cargoRequests.id),
  relatedOfferId: varchar("related_offer_id").references(() => tradingOffers.id),
  relatedRetradeId: varchar("related_retrade_id").references(() => retrades.id),
  relatedRequestNumber: varchar("related_request_number"),
  createdAt: timestamp("created_at").defaultNow(),
});

// Profile edit requests table
export const profileEditRequests = pgTable("profile_edit_requests", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  status: text("status").notNull().default("pending"), // pending, approved, rejected
  companyName: varchar("company_name", { length: 255 }),
  companyInn: varchar("company_inn", { length: 12 }),
  companyKpp: varchar("company_kpp", { length: 9 }),
  companyOgrn: varchar("company_ogrn", { length: 15 }),
  email: varchar("email", { length: 255 }),
  phone: varchar("phone", { length: 20 }),
  legalAddress: text("legal_address"),
  actualAddress: text("actual_address"),
  moderatedBy: varchar("moderated_by").references(() => users.id),
  moderatedAt: timestamp("moderated_at"),
  rejectionReason: text("rejection_reason"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertUserSchema = createInsertSchema(users).omit({
  id: true,
  createdAt: true,
});

export const insertRetradeOfferSchema = createInsertSchema(retradeOffers).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type RetradeOffer = typeof retradeOffers.$inferSelect;
export type InsertRetradeOffer = z.infer<typeof insertRetradeOfferSchema>;

export const insertCargoRequestSchema = z.object({
  status: z.string().default("draft"),

  // Route information
  departureStation: z.array(z.string()).min(1, "Выберите минимум одну станцию погрузки"),
  departureRailway: z.string().optional(),
  arrivalStation: z.array(z.string()).min(1, "Выберите минимум одну станцию выгрузки"),
  arrivalRailway: z.string().optional(),

  // Cargo information
  cargoName: z.string().min(1, "Наименование груза обязательно"),
  etsngsCode: z.string().min(1, "Код ЕТСНГ обязателен"),
  cargoWeight: z.coerce.number().min(1, "Вес груза должен быть больше 0"),
  wagonType: z.string().min(1, "Тип вагона обязателен"),
  wagonModel: z.string().optional(),
  additionalWagonRequirements: z.string().optional(),

  // Schedule and norms
  plannedDeparture: z.any(), // Принимаем любой тип для даты
  deliveryDays: z.coerce.number().optional().nullable(),
  loadingNorm: z.coerce.number().optional().nullable(),
  unloadingNorm: z.coerce.number().optional().nullable(),
  wagonCount: z.coerce.number().optional().nullable(),

  // Additional transportation parameters
  penaltyRate: z.coerce.number().optional().nullable(),
  penaltyAmount: z.coerce.number().optional().nullable(),
  moratoriumActive: z.boolean().optional(),
  wagonSubmissionFrom: z.any().optional().nullable(),
  wagonSubmissionTo: z.any().optional().nullable(),
  wagonSchedulePeriod: z.coerce.number().optional().nullable(),
  wagonScheduleCount: z.coerce.number().optional().nullable(),
  wagonRequirements: z.string().optional().nullable(),
  specialNotes: z.string().optional().nullable(),
  tradingCloseDateTime: z.any().optional().nullable(),

  // Wagon owner specific fields
  orderPeriod: z.string().optional(),
  provisionPeriod: z.string().optional(),
  loadingPeriod: z.string().optional(),
  restrictionsNotes: z.string().optional(),

  // Customer information
  customerName: z.string().optional(),
  customerInn: z.string().optional(),

  // System fields
  createdBy: z.string().min(1, "ID создателя обязателен"),
});

// Схема валидации для собственников вагонов (ППС)
export const insertWagonOwnerRequestSchema = z.object({
  status: z.string().default("draft"),

  // Route information - для ППС это опционально
  departureStation: z.array(z.string()).optional(),
  departureRailway: z.string().optional(),
  arrivalStation: z.array(z.string()).optional(),
  arrivalRailway: z.string().optional(),

  // Cargo information - для ППС не обязательно
  cargoName: z.string().optional(),
  etsngsCode: z.string().optional(),
  cargoWeight: z.coerce.number().optional().nullable(),
  wagonType: z.string().min(1, "Тип вагона обязателен"),
  wagonModel: z.string().optional(),
  additionalWagonRequirements: z.string().optional(),

  // Schedule and norms
  plannedDeparture: z.any().optional(),
  deliveryDays: z.coerce.number().optional().nullable(),
  loadingNorm: z.coerce.number().optional().nullable(),
  unloadingNorm: z.coerce.number().optional().nullable(),
  wagonCount: z.coerce.number().optional().nullable(),

  // Additional transportation parameters
  penaltyRate: z.coerce.number().optional().nullable(),
  penaltyAmount: z.coerce.number().optional().nullable(),
  moratoriumActive: z.boolean().optional(),
  wagonSubmissionFrom: z.any().optional().nullable(),
  wagonSubmissionTo: z.any().optional().nullable(),
  wagonSchedulePeriod: z.coerce.number().optional().nullable(),
  wagonScheduleCount: z.coerce.number().optional().nullable(),
  wagonRequirements: z.string().optional().nullable(),
  specialNotes: z.string().optional().nullable(),
  tradingCloseDateTime: z.any().optional().nullable(),

  // Wagon owner specific fields - обязательные для ППС
  orderPeriod: z.string().min(1, "Срок заявки обязателен"),
  orderPeriodEnd: z.string().min(1, "Окончание срока заявки обязательно"),
  provisionPeriod: z.string().min(1, "Срок предоставления обязателен"),
  provisionPeriodEnd: z.string().min(1, "Окончание срока предоставления обязательно"),
  loadingPeriod: z.string().min(1, "Срок погрузки обязателен"),
  loadingPeriodEnd: z.string().min(1, "Окончание срока погрузки обязательно"),
  restrictionsNotes: z.string().optional(),

  // Customer information
  customerName: z.string().optional(),
  customerInn: z.string().optional(),

  // System fields
  createdBy: z.string().min(1, "ID создателя обязателен"),
});

export const insertTradingOfferSchema = createInsertSchema(tradingOffers)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    cargoRequestId: z.string().min(1, "ID заявки обязателен"),
    pricePerWagon: z.coerce.number().min(0, "Цена должна быть положительной").optional(),
    availableWagons: z.coerce.number().min(1, "Количество вагонов должно быть больше 0").optional(),

    // Поля для переторжки
    isRetrade: z.boolean().optional(),
    originalOfferId: z.string().optional(),
    retradeTariff: z.coerce
      .number()
      .min(0, "Тариф переторжки должен быть положительным")
      .optional(),
    retradeComment: z.string().optional(),
  });

// Notification settings table
export const notificationSettings = pgTable("notification_settings", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" })
    .unique(),
  newRequest: boolean("new_request").default(true), // Новые заявки
  newOffer: boolean("new_offer").default(true), // Новые предложения
  tradingResults: boolean("trading_results").default(true), // Итоги торгов
  retrade: boolean("retrade").default(true), // Переторжка
  statusChanges: boolean("status_changes").default(true), // Изменения статусов
  systemNews: boolean("system_news").default(true), // Новости системы от модераторов
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertRetradeSchema = createInsertSchema(retrades)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    cargoRequestId: z.string().min(1, "ID заявки обязателен"),
    type: z.enum(["free_format", "descending_tender"], {
      errorMap: () => ({ message: "Выберите тип переторжки" }),
    }),
    departureStation: z.array(z.string()).min(1, "Выберите минимум одну станцию погрузки"),
    arrivalStation: z.array(z.string()).min(1, "Выберите минимум одну станцию выгрузки"),
    startDateTime: z.any(),
    endDateTime: z.any(),
  });

export const insertNotificationSchema = createInsertSchema(notifications).omit({
  id: true,
  createdAt: true,
});

export const insertNotificationSettingsSchema = createInsertSchema(notificationSettings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertUser = z.infer<typeof insertUserSchema>;
export type User = typeof users.$inferSelect;
export type NotificationSettings = typeof notificationSettings.$inferSelect;
export type InsertNotificationSettings = z.infer<typeof insertNotificationSettingsSchema>;
export type UserProfile = typeof userProfiles.$inferSelect;
export type InsertCargoRequest = z.infer<typeof insertCargoRequestSchema>;
export type InsertWagonOwnerRequest = z.infer<typeof insertWagonOwnerRequestSchema>;
export type CargoRequest = typeof cargoRequests.$inferSelect;

// New types for trading system
export type TradingOffer = typeof tradingOffers.$inferSelect;
export type InsertTradingOffer = z.infer<typeof insertTradingOfferSchema>;
export type Notification = typeof notifications.$inferSelect;
export type InsertNotification = z.infer<typeof insertNotificationSchema>;
export type Retrade = typeof retrades.$inferSelect;
export type InsertRetrade = z.infer<typeof insertRetradeSchema>;

export type RequestStatus =
  | "draft"
  | "review"
  | "published"
  | "in_trading"
  | "completed"
  | "cancelled"
  | "rejected";
export type UserRole = "cargo_owner" | "wagon_owner" | "admin" | "guest";
export type OfferStatus = "pending" | "accepted" | "rejected" | "withdrawn";

// Profile edit request schema
export const insertProfileEditRequestSchema = createInsertSchema(profileEditRequests).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  moderatedBy: true,
  moderatedAt: true,
});
export type InsertProfileEditRequest = z.infer<typeof insertProfileEditRequestSchema>;
export type ProfileEditRequest = typeof profileEditRequests.$inferSelect;

// Initiator responses table - ответы инициатора заявки на предложения участников
export const initiatorResponses = pgTable("initiator_responses", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  cargoRequestId: varchar("cargo_request_id")
    .notNull()
    .references(() => cargoRequests.id, { onDelete: "cascade" }),
  participantId: varchar("participant_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  initiatorId: varchar("initiator_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  routeTariffs: text("route_tariffs").notNull(), // JSON array с тарифами по направлениям [{station: string, tariff: number}]
  message: text("message"), // Дополнительное сообщение от инициатора
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertInitiatorResponseSchema = createInsertSchema(initiatorResponses)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    cargoRequestId: z.string().min(1, "ID заявки обязателен"),
    participantId: z.string().min(1, "ID участника обязателен"),
    initiatorId: z.string().min(1, "ID инициатора обязателен"),
    routeTariffs: z.string().min(1, "Тарифы по маршрутам обязательны"),
    message: z.string().optional(),
  });

export type InitiatorResponse = typeof initiatorResponses.$inferSelect;
export type InsertInitiatorResponse = z.infer<typeof insertInitiatorResponseSchema>;

// News articles table
export const newsArticles = pgTable("news_articles", {
  id: varchar("id").primaryKey(),
  title: varchar("title", { length: 255 }).notNull(),
  description: text("description").notNull(),
  publishDate: timestamp("publish_date"),
  isPublished: boolean("is_published").default(false),
  createdBy: varchar("created_by")
    .notNull()
    .references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertNewsArticleSchema = createInsertSchema(newsArticles)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    title: z.string().min(1, "Заголовок обязателен"),
    description: z.string().min(1, "Описание обязательно"),
    publishDate: z.string().optional(),
  });

export type NewsArticle = typeof newsArticles.$inferSelect;
export type InsertNewsArticle = z.infer<typeof insertNewsArticleSchema>;

// Retrade extension requests table - запросы на продление переторжки
export const retradeExtensionRequests = pgTable("retrade_extension_requests", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  cargoRequestId: varchar("cargo_request_id")
    .notNull()
    .references(() => cargoRequests.id, { onDelete: "cascade" }),
  requestNumber: text("request_number").notNull(), // Номер заявки для отображения
  initiatorId: varchar("initiator_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  extensionDays: integer("extension_days").notNull(), // Количество дней продления
  currentTradingStartDate: timestamp("current_trading_start_date"), // Начальный срок торгов
  currentTradingEndDate: timestamp("current_trading_end_date").notNull(), // Текущий срок торгов
  status: text("status").notNull().default("pending"), // pending, approved, rejected
  moderatedBy: varchar("moderated_by").references(() => users.id),
  moderatedAt: timestamp("moderated_at"),
  rejectionReason: text("rejection_reason"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Request cancellation requests table - запросы на отмену заявки
export const requestCancellationRequests = pgTable("request_cancellation_requests", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  cargoRequestId: varchar("cargo_request_id")
    .notNull()
    .references(() => cargoRequests.id, { onDelete: "cascade" }),
  requestNumber: text("request_number").notNull(), // Номер заявки для отображения
  initiatorId: varchar("initiator_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  cancellationReason: text("cancellation_reason").notNull(), // Причина отмены
  currentTradingStartDate: timestamp("current_trading_start_date"), // Начальный срок торгов
  currentTradingEndDate: timestamp("current_trading_end_date").notNull(), // Срок торгов
  status: text("status").notNull().default("pending"), // pending, approved, rejected
  moderatedBy: varchar("moderated_by").references(() => users.id),
  moderatedAt: timestamp("moderated_at"),
  rejectionReason: text("rejection_reason"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// User permissions table - индивидуальные разрешения для каждого пользователя
export const userPermissions = pgTable("user_permissions", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),

  // Основные разрешения на чтение
  canViewRequests: boolean("can_view_requests").default(true),
  canViewTrading: boolean("can_view_trading").default(true),
  canViewReports: boolean("can_view_reports").default(false),
  canViewUsers: boolean("can_view_users").default(false),
  canViewNotifications: boolean("can_view_notifications").default(true),

  // Разрешения на создание и модификацию
  canCreateRequests: boolean("can_create_requests").default(true),
  canEditOwnRequests: boolean("can_edit_own_requests").default(true),
  canDeleteOwnRequests: boolean("can_delete_own_requests").default(true),
  canCreateOffers: boolean("can_create_offers").default(true),
  canEditOwnOffers: boolean("can_edit_own_offers").default(true),
  canWithdrawOwnOffers: boolean("can_withdraw_own_offers").default(true),

  // Административные разрешения
  canModerateRequests: boolean("can_moderate_requests").default(false),
  canModerateOffers: boolean("can_moderate_offers").default(false),
  canManageUsers: boolean("can_manage_users").default(false),
  canCreateNews: boolean("can_create_news").default(false),
  canEditNews: boolean("can_edit_news").default(false),
  canDeleteNews: boolean("can_delete_news").default(false),
  canViewDrafts: boolean("can_view_drafts").default(false),

  // Специальные разрешения для торгов
  canViewAllOffers: boolean("can_view_all_offers").default(false),
  canSelectWinners: boolean("can_select_winners").default(false),
  canForceCloseTrading: boolean("can_force_close_trading").default(false),

  // Разрешения для контент-менеджмента
  canManageDictionaries: boolean("can_manage_dictionaries").default(false),
  canExportData: boolean("can_export_data").default(false),
  canImportData: boolean("can_import_data").default(false),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertUserPermissionsSchema = createInsertSchema(userPermissions).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type UserPermissions = typeof userPermissions.$inferSelect;
export type InsertUserPermissions = z.infer<typeof insertUserPermissionsSchema>;

// Schema for retrade extension requests
export const insertRetradeExtensionRequestSchema = createInsertSchema(retradeExtensionRequests)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    moderatedBy: true,
    moderatedAt: true,
  })
  .extend({
    cargoRequestId: z.string().min(1, "ID заявки обязателен"),
    requestNumber: z.string().min(1, "Номер заявки обязателен"),
    initiatorId: z.string().min(1, "ID инициатора обязателен"),
    extensionDays: z.coerce.number().min(1, "Количество дней должно быть больше 0"),
    currentTradingEndDate: z.any(),
  });

// Schema for request cancellation requests
export const insertRequestCancellationRequestSchema = createInsertSchema(
  requestCancellationRequests,
)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    moderatedBy: true,
    moderatedAt: true,
  })
  .extend({
    cargoRequestId: z.string().min(1, "ID заявки обязателен"),
    requestNumber: z.string().min(1, "Номер заявки обязателен"),
    initiatorId: z.string().min(1, "ID инициатора обязателен"),
    cancellationReason: z.string().min(1, "Причина отмены обязательна"),
    currentTradingEndDate: z.any(),
  });

export type RetradeExtensionRequest = typeof retradeExtensionRequests.$inferSelect;
export type InsertRetradeExtensionRequest = z.infer<typeof insertRetradeExtensionRequestSchema>;
export type RequestCancellationRequest = typeof requestCancellationRequests.$inferSelect;
export type InsertRequestCancellationRequest = z.infer<
  typeof insertRequestCancellationRequestSchema
>;
